/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Box, BoxSection } from 'components/_new/Box';
import { Button } from 'components/_new/Button';
import { Link } from 'components/_new/Link';
import { KnowledgeBaseArticleContentItem } from 'types/KnowledgeBase';
import { classNames } from 'primereact/utils';
import { slugify } from 'utils/helpers';
import openIcon from 'assets/icons/actions/open.svg';
import viewIcon from 'assets/icons/actions/view.svg';
import { Tag } from 'components/_new/Tag';
import { ReadedIcon } from './ReadedIcon';
import { ToreadIcon } from './ToreadIcon';
import { FavoriteIcon } from './FavoriteIcon';
import { ColoredIcon } from './ColoredIcon';
import { getKnowledgeBaseArticleIcon } from './utils/utils';

type ArticleItemProps = {
	data: KnowledgeBaseArticleContentItem;
	onPreviewClick?: () => void;
	linkPrefix: string;
	bordered?: boolean;
	showButtonsOnHover?: boolean;
	openPreviewOnClick?: boolean;
	slim?: boolean;
	renderTitle?: (title: string) => ReactNode;
};

export const ArticleItem = ({
	data,
	onPreviewClick,
	linkPrefix,
	bordered = false,
	showButtonsOnHover = false,
	openPreviewOnClick = false,
	slim = false,
	renderTitle,
}: ArticleItemProps) => {
	const { t } = useTranslation();

	const { articleId, slug, title, usage, article, contentType, category: topCategory } = data;
	const timeToRead = data.info?.timeToRead;
	const category = topCategory || article?.category || null;
	const to = `${linkPrefix}/article/${articleId}${slug ? `-${slugify(slug)}` : ''}`;

	const navigate = useNavigate();

	return (
		<Box
			className={classNames('box-file-container align-items-center pl-3 pr-2', {
				'hover-focus-area-lg': showButtonsOnHover,
				'md:py-1': !slim,
				'py-0': slim,
			})}
			style={{ minHeight: '60px' }}
			variant={bordered ? 'white-bordered' : 'white'}
			vertical
			onClick={(event: any) => {
				if (window.innerWidth <= 768) {
					const target = event.target as HTMLElement;
					if (!target.closest('a') && !target.closest('.p-button')) {
						if (openPreviewOnClick && onPreviewClick) {
							event.preventDefault();
							onPreviewClick();
						} else {
							navigate(to);
						}
					}
				}
			}}
		>
			<BoxSection
				className="flex-grow flex align-items-center overflow-hidden"
				contentClassName="w-full"
				noPad={slim}
			>
				<div className="flex align-items-center gap-3 w-full overflow-hidden">
					<ColoredIcon src={getKnowledgeBaseArticleIcon(category, contentType)} size="26px" color="#5035c1" />
					<div className="flex flex-column gap-1 overflow-hidden flex-grow">
						<div className="flex align-items-center gap-2 overflow-hidden">
							<Link
								to={openPreviewOnClick && onPreviewClick ? '#' : to}
								label={title}
								variant="grey"
								noUnderline
								className="block text-l font-bold white-space-nowrap overflow-hidden text-overflow-ellipsis"
								onClick={(event) => {
									if (openPreviewOnClick && onPreviewClick) {
										event.preventDefault();
										onPreviewClick();
									}
								}}
								scrollToTop={!(openPreviewOnClick && onPreviewClick)}
							>
								{renderTitle ? renderTitle(title) : title}
							</Link>
							{timeToRead && (
								<span className="text-sm font-medium white-space-nowrap" style={{ color: '#9CA3AF' }}>
									({timeToRead} min)
								</span>
							)}
							<div className="flex align-items-center gap-1 flex-shrink-0" style={{ marginTop: '2px' }}>
								{data.isProfessional && (
									<Tag variant="blue" bold min className="hidden md:inline-flex">
										{t('knowledgeBase.isProfessional')}
									</Tag>
								)}
								{typeof data.info?.flag?.favorite !== 'undefined' && data.info.flag.favorite && (
									<span
										title={t('knowledgeBase.favorite.favorite')}
										className="flex align-items-center"
									>
										<FavoriteIcon checked size={16} />
									</span>
								)}
								{typeof data.info?.flag?.readed !== 'undefined' && data.info.flag.readed && (
									<span title={t('knowledgeBase.readed.readed')} className="flex align-items-center">
										<ReadedIcon checked size={16} />
									</span>
								)}
								{typeof data.info?.flag?.toRead !== 'undefined' && data.info.flag.toRead && (
									<span title={t('knowledgeBase.toRead.toRead')} className="flex align-items-center">
										<ToreadIcon checked size={16} />
									</span>
								)}
							</div>
						</div>

						{usage && (
							<div
								className="text-xs white-space-nowrap overflow-hidden text-overflow-ellipsis"
								style={{ color: '#6B7280', marginTop: '-2px' }}
							>
								{usage}
							</div>
						)}
					</div>
				</div>
			</BoxSection>

			<BoxSection
				horizontal
				className="flex-shrink-0 right-col"
				contentClassName={classNames('flex flex-nowrap gap-1 align-items-center justify-content-end', {
					'buttons-visible': !showButtonsOnHover,
					'hover-focus-view': showButtonsOnHover,
				})}
				noPad={slim}
			>
				<Button
					label="Open in new tab"
					icon={<ColoredIcon src={openIcon} size="20px" />}
					iconOnly
					variant="primary-text"
					variantSize="lg"
					noPad={slim}
					style={{ color: '#494B69' }}
					onClick={() => {
						const absoluteUrl = to.startsWith('/')
							? `${window.location.origin}${to}`
							: `${window.location.origin}/${to}`;
						window.open(absoluteUrl, '_blank');
					}}
				/>
				{onPreviewClick && (
					<Button
						label="View"
						icon={<ColoredIcon src={viewIcon} size="24px" />}
						iconOnly
						variant="primary-text"
						variantSize="lg"
						noPad={slim}
						style={{ color: '#494B69' }}
						onClick={(event: any) => {
							event.preventDefault();
							onPreviewClick();
						}}
					/>
				)}
			</BoxSection>
		</Box>
	);
};
